// Dishes levels. Coordinates: 1200 x 800. The ball is a soap ball, the goal is the washing-up bowl.
// The id is fixed (progress is saved under it) – never change it once the level is released.
import { defineLevels } from '../../core/registry';
import { on } from '../../core/level-helpers';
import type { LevelData, ObjSpec, StaticData } from '../../core/types';

// a sink with water: bottom + two walls + water
function sink(x: number, y: number, w: number, h: number): { statics: StaticData[]; water: ObjSpec } {
  return {
    statics: [
      { x, y: y + h / 2 + 10, w: w + 20, h: 20, skin: 'dishes_sink' },
      { x: x - w / 2 - 2, y: y + 6, w: 16, h: h + 28, skin: 'dishes_sink' },
      { x: x + w / 2 + 2, y: y + 6, w: 16, h: h + 28, skin: 'dishes_sink' },
    ],
    water: { t: 'dishes_water', x, y: y + 8, w: w - 12, h: h - 4 },
  };
}
const B = 15; // soap ball radius
const L: LevelData[] = [];

{ // 1 – First roll – build a bridge over the washing-up water
  const s1: StaticData = { x: 220, y: 280, w: 360, h: 22, a: 9, skin: 'dishes_rack' };
  const s2: StaticData = { x: 760, y: 400, w: 400, h: 22, a: 5, skin: 'dishes_rack' };
  const v = sink(480, 640, 190, 110);
  L.push({
    id: 'first-roll', par: 1, tools: { plank: 1 }, learn: ['gravity', 'ramp'],
    statics: [s1, s2, ...v.statics, { x: 1040, y: 600, w: 250, h: 34, skin: 'dishes_table' }, { x: 1150, y: 420, w: 44, h: 330, skin: 'dishes_stack' }],
    balls: [on(s1, -150, B)],
    goal: { type: 'bowl', x: 1040, y: 583, w: 170 },
    objs: [v.water, { t: 'duck', x: 1085, y: 440 }],
    solution: [{ t: 'plank', x: 480, y: 345, a: 15 }],
  });
}
{ // 2 – The drip – new tool: the tray; the ball falls straight down
  const v = sink(330, 660, 220, 110);
  L.push({
    id: 'drip', par: 1, tools: { dishes_tray: 1 }, learn: ['gravity'], intro: 'dishes_tray',
    statics: [...v.statics, { x: 760, y: 660, w: 400, h: 34, skin: 'dishes_table' }],
    balls: [{ x: 330, y: 150, r: B }],
    goal: { type: 'bowl', x: 660, y: 643, w: 210 },
    objs: [{ t: 'dishes_tap', x: 330, y: 150, dir: 1 }, v.water, { t: 'duck', x: 620, y: 470 }],
    solution: [{ t: 'dishes_tray', x: 420, y: 420, a: 20 }],
  });
}
{ // 3 – The other way – the tray has to slope to the left
  const v = sink(820, 660, 220, 110);
  L.push({
    id: 'other-way', par: 1, tools: { dishes_tray: 1 }, learn: ['gravity', 'direction'],
    statics: [...v.statics, { x: 440, y: 660, w: 400, h: 34, skin: 'dishes_table' }],
    balls: [{ x: 820, y: 150, r: B }],
    goal: { type: 'bowl', x: 540, y: 643, w: 210 },
    objs: [{ t: 'dishes_tap', x: 820, y: 150, dir: -1 }, v.water, { t: 'duck', x: 560, y: 470 }],
    solution: [{ t: 'dishes_tray', x: 730, y: 420, a: -20 }],
    fails: [[{ t: 'dishes_tray', x: 730, y: 420, a: 20 }]],
  });
}

{ // 4 – Pour some more – flat tray = too little speed to get up over the board
  const v = sink(250, 660, 200, 110);
  L.push({
    id: 'pour-more', par: 1, tools: { dishes_tray: 1 }, learn: ['gravity', 'ramp'],
    statics: [...v.statics, { x: 560, y: 557, w: 360, h: 34, skin: 'dishes_table' }, { x: 810, y: 530, w: 160, h: 18, a: -15, skin: 'dishes_board' },
      { x: 1010, y: 640, w: 260, h: 34, skin: 'dishes_table' }, { x: 1150, y: 480, w: 44, h: 300, skin: 'dishes_stack' }],
    balls: [{ x: 260, y: 130, r: B }],
    goal: { type: 'bowl', x: 1000, y: 623, w: 190 },
    objs: [{ t: 'dishes_tap', x: 260, y: 130, dir: 1 }, v.water, { t: 'duck', x: 900, y: 420 }],
    solution: [{ t: 'dishes_tray', x: 330, y: 400, a: 30 }],
    fails: [[{ t: 'dishes_tray', x: 330, y: 400, a: 10 }], [{ t: 'dishes_tray', x: 330, y: 400, a: 0 }]],
  });
}

{ // 5 – The tea towel – same level, but now a tea towel brakes; only a steep tray gives enough speed
  const v = sink(250, 660, 200, 110);
  L.push({
    id: 'tea-towel', par: 1, tools: { dishes_tray: 1 }, learn: ['gravity', 'ramp', 'friction'],
    statics: [...v.statics, { x: 590, y: 557, w: 420, h: 34, skin: 'dishes_cloth', drag: 0.98 },
      { x: 960, y: 680, w: 240, h: 34, skin: 'dishes_table' }, { x: 1110, y: 520, w: 44, h: 300, skin: 'dishes_stack' }],
    balls: [{ x: 260, y: 130, r: B }],
    goal: { type: 'bowl', x: 950, y: 663, w: 200 },
    objs: [{ t: 'dishes_tap', x: 260, y: 130, dir: 1 }, v.water, { t: 'duck', x: 1000, y: 420 }],
    solution: [{ t: 'dishes_tray', x: 330, y: 400, a: 30 }],
    fails: [[{ t: 'dishes_tray', x: 330, y: 400, a: 10 }], [{ t: 'dishes_tray', x: 330, y: 400, a: 0 }]],
  });
}

{ // 6 – The sponge – new tool: the sponge; the steep ramp gives too much speed – brake!
  const s1: StaticData = { x: 150, y: 170, w: 220, h: 20, a: 10, skin: 'dishes_rack' };
  const v = sink(990, 660, 180, 110);
  L.push({
    id: 'sponge', par: 1, tools: { dishes_sponge: 1, dishes_tray: 1 }, learn: ['ramp', 'friction'], intro: 'dishes_sponge',
    statics: [s1, { x: 400, y: 340, w: 340, h: 18, a: 35, skin: 'dishes_board' }, { x: 600, y: 557, w: 240, h: 34, skin: 'dishes_table' },
      { x: 800, y: 680, w: 150, h: 34, skin: 'dishes_table' }, ...v.statics],
    balls: [on(s1, -70, B)],
    goal: { type: 'bowl', x: 800, y: 663, w: 140 },
    objs: [v.water, { t: 'duck', x: 990, y: 470 }],
    solution: [{ t: 'dishes_sponge', x: 370, y: 290, a: 30 }],
    fails: [[{ t: 'dishes_tray', x: 600, y: 520, a: 0 }]],
  });
}

{ // 7 – The mirror tray – same idea mirrored, and now there is a chopping board to choose too (decoy)
  const v = sink(950, 660, 200, 110);
  L.push({
    id: 'mirror-tray', par: 1, tools: { dishes_tray: 1, plank: 1 }, learn: ['gravity', 'ramp', 'direction'],
    statics: [...v.statics, { x: 640, y: 557, w: 360, h: 34, skin: 'dishes_table' }, { x: 390, y: 530, w: 160, h: 18, a: 15, skin: 'dishes_board' },
      { x: 190, y: 640, w: 260, h: 34, skin: 'dishes_table' }, { x: 50, y: 480, w: 44, h: 300, skin: 'dishes_stack' }],
    balls: [{ x: 940, y: 130, r: B }],
    goal: { type: 'bowl', x: 200, y: 623, w: 190 },
    objs: [{ t: 'dishes_tap', x: 940, y: 130, dir: -1 }, v.water, { t: 'duck', x: 300, y: 420 }],
    solution: [{ t: 'dishes_tray', x: 870, y: 400, a: -30 }],
    fails: [[{ t: 'dishes_tray', x: 870, y: 400, a: -10 }], [{ t: 'dishes_tray', x: 870, y: 400, a: 0 }], [{ t: 'dishes_tray', x: 870, y: 400, a: 30 }]],
  });
}
{ // 8 – Zigzag – the ball has to change direction twice
  const sh: StaticData = { x: 420, y: 400, w: 520, h: 22, skin: 'dishes_rack' };
  L.push({
    id: 'zigzag', par: 2, tools: { dishes_tray: 2, plank: 1 }, learn: ['gravity', 'direction', 'ramp'],
    statics: [sh, { x: 470, y: 700, w: 340, h: 34, skin: 'dishes_table' }, { x: 1020, y: 560, w: 44, h: 420, skin: 'dishes_stack' }],
    balls: [{ x: 250, y: 130, r: B }],
    goal: { type: 'bowl', x: 570, y: 683, w: 200 },
    objs: [{ t: 'dishes_tap', x: 250, y: 130, dir: 1 }, { t: 'duck', x: 760, y: 360 }],
    solution: [{ t: 'dishes_tray', x: 320, y: 300, a: 20 }, { t: 'dishes_tray', x: 800, y: 540, a: -20 }],
    fails: [[{ t: 'dishes_tray', x: 320, y: 300, a: 20 }], [{ t: 'dishes_tray', x: 320, y: 300, a: 0 }, { t: 'dishes_tray', x: 800, y: 540, a: -20 }]],
  });
}

{ // 9 – Speed up – don't brake, speed! (the sponge is a decoy)
  const v = sink(250, 660, 200, 110);
  L.push({
    id: 'speed-up', par: 1, tools: { dishes_sponge: 1, dishes_tray: 1 }, learn: ['gravity', 'ramp'],
    statics: [...v.statics, { x: 560, y: 557, w: 360, h: 34, skin: 'dishes_table' }, { x: 810, y: 530, w: 160, h: 18, a: -15, skin: 'dishes_board' },
      { x: 1010, y: 640, w: 260, h: 34, skin: 'dishes_table' }, { x: 1150, y: 480, w: 44, h: 300, skin: 'dishes_stack' }],
    balls: [{ x: 260, y: 130, r: B }],
    goal: { type: 'bowl', x: 1000, y: 623, w: 190 },
    objs: [{ t: 'dishes_tap', x: 260, y: 130, dir: 1 }, v.water, { t: 'duck', x: 560, y: 470 }],
    solution: [{ t: 'dishes_tray', x: 330, y: 400, a: 30 }],
    fails: [[{ t: 'dishes_sponge', x: 300, y: 400, a: 30 }], [{ t: 'dishes_tray', x: 330, y: 400, a: 10 }]],
  });
}
{ // 10 – The ladle hop – new tool: the ladle; speed down the ramp becomes a jump
  const s1: StaticData = { x: 140, y: 190, w: 200, h: 20, a: 10, skin: 'dishes_rack' };
  L.push({
    id: 'ladle-hop', par: 1, tools: { dishes_ladle: 1, dishes_tray: 1 }, learn: ['gravity', 'energy'], intro: 'dishes_ladle',
    statics: [s1, { x: 380, y: 400, w: 400, h: 18, a: 35, skin: 'dishes_board' }, { x: 590, y: 600, w: 460, h: 34, skin: 'dishes_table' },
      { x: 830, y: 538, w: 44, h: 90, skin: 'dishes_stack' }, { x: 960, y: 680, w: 280, h: 34, skin: 'dishes_table' }, { x: 1130, y: 540, w: 44, h: 260, skin: 'dishes_stack' }],
    balls: [on(s1, -60, B)],
    goal: { type: 'bowl', x: 960, y: 663, w: 200 },
    objs: [{ t: 'duck', x: 760, y: 300 }],
    solution: [{ t: 'dishes_ladle', x: 590, y: 575, a: 0 }],
    fails: [[{ t: 'dishes_tray', x: 660, y: 540, a: -20 }]],
  });
}

{ // 11 – Speed for a hop – build the bridge down to the ramp, but don't brake: the jump needs speed
  const s1: StaticData = { x: 130, y: 150, w: 200, h: 20, a: 10, skin: 'dishes_rack' };
  L.push({
    id: 'speed-to-hop', par: 1, tools: { dishes_tray: 1, dishes_sponge: 1 }, learn: ['gravity', 'energy', 'ramp'],
    statics: [s1, { x: 470, y: 514, w: 240, h: 18, a: 35, skin: 'dishes_board' }, { x: 640, y: 600, w: 360, h: 34, skin: 'dishes_table' },
      { x: 880, y: 538, w: 44, h: 90, skin: 'dishes_stack' }, { x: 1020, y: 680, w: 240, h: 34, skin: 'dishes_table' }, { x: 1155, y: 540, w: 44, h: 260, skin: 'dishes_stack' }],
    fixed: [{ t: 'dishes_ladle', x: 680, y: 589, a: 0 }],
    balls: [on(s1, -60, B)],
    goal: { type: 'bowl', x: 1020, y: 663, w: 200 },
    objs: [{ t: 'duck', x: 860, y: 330 }],
    solution: [{ t: 'dishes_tray', x: 300, y: 230, a: 40 }],
    fails: [[{ t: 'dishes_sponge', x: 320, y: 280, a: 30 }], [{ t: 'dishes_tray', x: 300, y: 230, a: 20 }]],
  });
}

{ // 12 – Over the sink – build both the ramp and the jump yourself
  const s1: StaticData = { x: 130, y: 150, w: 200, h: 20, a: 10, skin: 'dishes_rack' };
  const v = sink(905, 650, 230, 120);
  L.push({
    id: 'over-the-sink', par: 2, tools: { dishes_tray: 2, dishes_ladle: 1, dishes_sponge: 1 }, learn: ['gravity', 'energy', 'ramp'],
    statics: [s1, { x: 470, y: 514, w: 240, h: 18, a: 35, skin: 'dishes_board' }, { x: 620, y: 600, w: 320, h: 34, skin: 'dishes_table' },
      ...v.statics, { x: 1100, y: 640, w: 140, h: 34, skin: 'dishes_table' }, { x: 1160, y: 500, w: 30, h: 260, skin: 'dishes_stack' }],
    balls: [on(s1, -60, B)],
    goal: { type: 'bowl', x: 1085, y: 623, w: 130 },
    objs: [v.water, { t: 'duck', x: 900, y: 360 }],
    solution: [{ t: 'dishes_tray', x: 300, y: 230, a: 40 }, { t: 'dishes_ladle', x: 680, y: 589, a: 0 }],
    fails: [[{ t: 'dishes_tray', x: 300, y: 230, a: 40 }], [{ t: 'dishes_tray', x: 300, y: 230, a: 40 }, { t: 'dishes_tray', x: 700, y: 560, a: -20 }]],
  });
}

{ // 13 – The long way round – three trays, the ball changes direction all the way down
  const A: StaticData = { x: 700, y: 300, w: 300, h: 20, skin: 'dishes_rack' };
  const Bs: StaticData = { x: 640, y: 520, w: 320, h: 20, skin: 'dishes_rack' };
  const v = sink(1000, 680, 220, 110);
  L.push({
    id: 'long-way', par: 3, tools: { dishes_tray: 3, plank: 1 }, learn: ['gravity', 'direction', 'ramp'],
    statics: [A, Bs, ...v.statics, { x: 560, y: 740, w: 360, h: 34, skin: 'dishes_table' }, { x: 360, y: 640, w: 40, h: 170, skin: 'dishes_stack' }],
    balls: [{ x: 1000, y: 130, r: B }],
    goal: { type: 'bowl', x: 600, y: 723, w: 200 },
    objs: [{ t: 'dishes_tap', x: 1000, y: 130, dir: -1 }, v.water, { t: 'duck', x: 900, y: 450 }],
    solution: [{ t: 'dishes_tray', x: 930, y: 260, a: -20 }, { t: 'dishes_tray', x: 450, y: 420, a: 30 }, { t: 'dishes_tray', x: 860, y: 600, a: -20 }],
    fails: [[{ t: 'dishes_tray', x: 930, y: 260, a: -20 }, { t: 'dishes_tray', x: 450, y: 420, a: 30 }], [{ t: 'dishes_tray', x: 930, y: 260, a: -20 }, { t: 'dishes_tray', x: 860, y: 600, a: -20 }]],
  });
}

{ // 14 – The glove – ramp + spring (from Breakfast); the ball has to get up over the stack of plates
  L.push({
    id: 'glove', par: 2, tools: { dishes_tray: 1, pad: 1, dishes_sponge: 1 }, learn: ['gravity', 'spring', 'ramp'],
    statics: [{ x: 450, y: 760, w: 700, h: 34, skin: 'dishes_table' }, { x: 700, y: 650, w: 44, h: 200, skin: 'dishes_stack' },
      { x: 950, y: 600, w: 300, h: 34, skin: 'dishes_table' }, { x: 1120, y: 470, w: 40, h: 230, skin: 'dishes_stack' }],
    balls: [{ x: 200, y: 130, r: B }],
    goal: { type: 'bowl', x: 950, y: 583, w: 200 },
    objs: [{ t: 'dishes_tap', x: 200, y: 130, dir: 1 }, { t: 'duck', x: 700, y: 300 }],
    solution: [{ t: 'dishes_tray', x: 270, y: 380, a: 30 }, { t: 'pad', x: 520, y: 620, a: 0 }],
    fails: [[{ t: 'dishes_tray', x: 270, y: 380, a: 30 }], [{ t: 'pad', x: 520, y: 620, a: 0 }], [{ t: 'dishes_tray', x: 270, y: 380, a: 30 }, { t: 'dishes_sponge', x: 520, y: 720, a: 0 }]],
  });
}

{ // 15 – The masterpiece – all of the dishes; several ways to the bowl
  const s1: StaticData = { x: 140, y: 140, w: 220, h: 20, a: 10, skin: 'dishes_rack' };
  const v = sink(440, 700, 340, 90);
  L.push({
    id: 'masterpiece', par: 3, tools: { dishes_tray: 2, dishes_ladle: 1, pad: 1, dishes_sponge: 1 }, learn: ['gravity', 'ramp', 'energy', 'spring'], time: 26,
    statics: [s1, { x: 600, y: 470, w: 300, h: 34, skin: 'dishes_table' }, ...v.statics, { x: 870, y: 640, w: 44, h: 300, skin: 'dishes_stack' },
      { x: 1030, y: 680, w: 240, h: 34, skin: 'dishes_table' }, { x: 1160, y: 540, w: 30, h: 260, skin: 'dishes_stack' }],
    balls: [on(s1, -70, B)],
    goal: { type: 'bowl', x: 1020, y: 663, w: 190 },
    objs: [v.water, { t: 'duck', x: 980, y: 300 }],
    solution: [{ t: 'dishes_tray', x: 300, y: 220, a: 40 }, { t: 'dishes_tray', x: 454, y: 354, a: 40 }, { t: 'dishes_ladle', x: 640, y: 459, a: 0 }],
    fails: [[{ t: 'dishes_tray', x: 300, y: 220, a: 40 }, { t: 'dishes_tray', x: 454, y: 354, a: 40 }]],
  });
}

export const levels = defineLevels(L);
